// Invented, fixed demo data for the public /demo page. Never read from any account.
import { FIELD_METRICS, classifyReading, type ReadingStatus } from "@/lib/field-metrics";

export interface SampleSite {
  id: string;
  name: string;
  city: string;
  state: string;
  /** Per-metric baseline [center, swing] used by the generator. */
  profile: Record<string, [number, number]>;
}

export interface SampleReading {
  siteId: string;
  metric: string;
  day: number; // 0 = 13 days ago … 13 = today
  value: number;
  status: ReadingStatus;
}

export const DEMO_DAYS = 14;

export const SAMPLE_SITES: SampleSite[] = [
  { id: "demo-site-1", name: "Harbor Point Industrial", city: "Portland", state: "OR",
    profile: { temperature: [16, 6], ph: [7.4, 0.9], dissolved_oxygen: [8, 3], turbidity: [3, 3], conductivity: [900, 500], voc: [20, 35] } },
  { id: "demo-site-2", name: "Cedar Creek Retail Lot", city: "Boise", state: "ID",
    profile: { temperature: [14, 5], ph: [7.1, 0.4], dissolved_oxygen: [9, 2], turbidity: [2, 1.5], conductivity: [600, 250], voc: [8, 6] } },
  { id: "demo-site-3", name: "Old Mill Riverfront", city: "Spokane", state: "WA",
    profile: { temperature: [12, 4], ph: [6.9, 0.6], dissolved_oxygen: [6.5, 2.5], turbidity: [4.5, 4], conductivity: [1300, 600], voc: [30, 30] } },
  { id: "demo-site-4", name: "Desert Fleet Yard", city: "Tucson", state: "AZ",
    profile: { temperature: [27, 7], ph: [7.8, 0.7], dissolved_oxygen: [7, 2], turbidity: [1.5, 1], conductivity: [1100, 400], voc: [40, 45] } },
];

// Deterministic PRNG (mulberry32) so every visit shows the same data.
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const NON_NEGATIVE = new Set(["voc", "turbidity", "conductivity", "dissolved_oxygen"]);

export function generateSampleReadings(): SampleReading[] {
  const out: SampleReading[] = [];
  SAMPLE_SITES.forEach((site, si) => {
    FIELD_METRICS.forEach((m, mi) => {
      const rand = rng(1000 + si * 97 + mi * 13);
      const [center, swing] = site.profile[m.key];
      for (let day = 0; day < DEMO_DAYS; day++) {
        let value = center + (rand() * 2 - 1) * swing;
        if (NON_NEGATIVE.has(m.key)) value = Math.max(0, value);
        value = Math.round(value * 100) / 100;
        out.push({ siteId: site.id, metric: m.key, day, value, status: classifyReading(m.key, value) });
      }
    });
  });
  return out;
}

export const SAMPLE_READINGS: SampleReading[] = generateSampleReadings();
